'use client';

import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { addQueryParams, removeQueryParams } from '@framework/router/nextRouter';
import { IFilterClickContext, IFilterSet } from '@/creditflowresearch/ui/common/hooks/filters/useFilterButtons';
import { useCorporateMarketFilters } from './useCorporateMarketFilters';
import { EuropeanMarket, UsMarket } from '@/creditflowresearch/domain/Market';
import cfrUrl from '@/creditflowresearch/infrastructure/url';
import { getDefaultCorporateSegments } from '@/creditflowresearch/application/helpers/getCorporatePageDefaultParams';
import { MarketAbbreviations } from '@/creditflowresearch/domain/Market/types/MarketType';
import { ACTIVE_TAB_QUERY_PARAM } from '@/creditflowresearch/ui/components/ActiveCalendar/US/usActiveCalendarTabsConfig';

interface IMarketRedirectConfig {
  marketId: string,
  getUrl: () => string,
}

const redirectConfigMap: Record<string, IMarketRedirectConfig> = {
  [UsMarket.ABBREVIATION]: {
    marketId: EuropeanMarket.MARKET_ID,
    getUrl: () => cfrUrl.getEuropeActiveCalendarUrl(),
  },
  [EuropeanMarket.ABBREVIATION]: {
    marketId: UsMarket.MARKET_ID,
    getUrl: () => cfrUrl.getCorporateActiveCalendarUrl(),
  },
};

export const useCorporateActiveCalendarFilters = (): IFilterSet[] => {
  const { push, query: { markets } } = useRouter();
  const baseFilters = useCorporateMarketFilters();
  const redirectConfig = redirectConfigMap[markets as string];

  return useMemo(() => baseFilters.map((filterSet) => {
    if (filterSet.key !== 'markets') {
      return filterSet;
    }

    return {
      ...filterSet,
      items: filterSet.items.map((item) => {
        if (item.id !== redirectConfig.marketId) {
          return item;
        }

        return {
          ...item,
          onClick: ({ searchQuery, filterName, queryName }: IFilterClickContext) => {
            const parsedQuery = removeQueryParams(
              Object.fromEntries(new URLSearchParams(searchQuery)),
              [ACTIVE_TAB_QUERY_PARAM],
            );
            const segments = getDefaultCorporateSegments(parsedQuery.products, queryName as MarketAbbreviations);
            const newQuery = addQueryParams(parsedQuery, { [filterName]: queryName, segments });

            push({ pathname: redirectConfig.getUrl(), query: newQuery }, undefined, { shallow: true });
          },
        };
      }),
    };
  }), [baseFilters, redirectConfig, push]);
};
